import { CardStatus } from '@/types/Card';
import { YapMoney } from '@/types/general';
import { PostalAddress } from '@/types/address';

export const supportedDigitalWallets = ['APPLE_PAY', 'ANDROID_PAY', 'SAMSUNG_PAY'];
export const cardsSortOrder = [
  CardStatus.New,
  CardStatus.Funded,
  CardStatus.Active,
  CardStatus.Frozen,
  CardStatus.Locked,
  CardStatus.Deleted
];

export function removeEmpty(obj: object) {
  return Object.entries(obj)
    .filter(([_, v]) => v != null)
    .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
}

export function removeAllEmptyFromObject(obj: object): object {
  if (Array.isArray(obj)) {
    return obj.filter((v) => !['', null, undefined].includes(v));
  } else if (typeof obj === 'object') {
    return Object.entries(obj)
      .filter(([_, v]) => v !== '' && v !== null && v !== undefined)
      .reduce((acc, [k, v]) => ({ ...acc, [k]: removeAllEmptyFromObject(v) }), {});
  } else {
    return obj;
  }
}

export function removeEmptyStringFromObject(obj: object): any {
  if (Array.isArray(obj)) {
    return obj.map((v) => removeEmptyStringFromObject(v));
  } else if (typeof obj === 'object') {
    return Object.entries(obj)
      .filter(([_, v]) => v !== '')
      .reduce((acc, [k, v]) => ({ ...acc, [k]: removeEmptyStringFromObject(v) }), {});
  } else {
    return obj;
  }
}

export const IDLE_TABS_SYNC_BROADCAST_CHANNEL = 'IDLE_TAB_SYNC';

export enum BroadcastMessageTypes {
  IdleData = 'IdleData',
  Logout = 'Logout'
}

export const REFRESH_TOKEN_EXP = 'refreshTokenExp';

export enum SseListener {
  DebitCard = 'debit_card',
  CardProgram = 'card_program',
  CardProgramContract = 'card_program_contract',
  CardInCardProgram = 'card_in_card_program',
  AgentInPartnership = 'agent_in_partnership',
  ExportTicket = 'export_ticket',
  CardBulkOrder = 'card_bulk_order',
  CashSubAccount = 'cash_sub_account',
  CashAccountCreated = 'cash_account_contract',
  EntitlementChanged = 'entitlement',
  Beneficiary = 'beneficiary',
  PaymentOrder = 'payment_order',
  TrxReceipt = 'transaction_receipt'
}

export interface YapAmount {
  value: number;
  factor: number;
}

export function factoredYapAmount(data: YapAmount, asNumber: true): number | undefined;
export function factoredYapAmount(data: YapAmount, asNumber?: false): string | undefined;
export function factoredYapAmount(data: YapAmount, asNumber = false): string | number | undefined {
  if (!data || data.value == null || data.factor == null || isNaN(data.value) || isNaN(data.factor)) {
    return undefined;
  }
  const { value, factor } = data;

  const isNegative = value < 0;
  const valueStr = Math.abs(value).toString();
  const integral = factor > 0 ? valueStr.slice(0, -factor) || '0' : valueStr;
  const decimals = factor > 0 ? valueStr.slice(-factor).padStart(factor, '0') : '';
  const result = `${isNegative ? '-' : ''}${integral}${decimals && `.${decimals}`}`;

  return asNumber ? Number(result) : result;
}

export function addYapAmount(a: YapAmount, b: YapAmount): YapAmount {
  // Determine the larger factor
  const factor = Math.max(a.factor, b.factor);

  // Normalize the values to the larger factor using BigInt for precision
  const aValue = BigInt(a.value) * BigInt(10 ** (factor - a.factor));
  const bValue = BigInt(b.value) * BigInt(10 ** (factor - b.factor));

  // Add the normalized values
  const value = aValue + bValue;

  // Return the result as a new YapAmount, converting BigInt back to number
  return { factor, value: Number(value) };
}

export function stringToYapAmount(str: string): YapAmount {
  const parsedStr = parseFloat(str);
  // Special case for exact zero values
  if (parsedStr === 0) {
    return { factor: 2, value: 0 };
  }

  let factor = str.includes('.') ? str.split('.')[1].replace(/0+$/, '').length : 0;
  factor = Math.max(factor, 2);

  const multiplier = Math.pow(10, factor);
  const value = parseFloat((parsedStr * multiplier).toFixed(0));

  return { factor, value };
}

export function stringToYapMoney(str: string, currency: string): YapMoney {
  return {
    amount: stringToYapAmount(str),
    currency
  };
}

export function isValidUUID(uuid: string): boolean {
  const uuidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return uuidPattern.test(uuid);
}

export function toProperCase(str: string): string {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function generatePostalAddress(address: PostalAddress, separator = '<br>') {
  const lines = [];
  if (address.street || address.number) {
    lines.push(`${address.street} ${address.number}`);
  }
  if (address.postBoxAddr) {
    lines.push(address.postBoxAddr);
  }
  if (address.postalCode || address.city) {
    lines.push(`${address.postalCode} ${address.city}`);
  }
  if (address.state) {
    lines.push(address.state);
  }
  if (address.province) {
    lines.push(address.province);
  }
  if (address.country) {
    lines.push(address.country);
  }
  return lines.join(separator);
}
